<template>
  <div>
    <!-- Parameters -->
    <div class="vx-row mb-base">
      <div class="vx-col w-full">
        <vx-card ref="parameters" title="Parameters">
          <div class="vx-row">
            <div class="vx-col w-full sm:w-1/2">
              <p class="pb-2 font-semibold">Underlying</p>
              <sp-global-input-search v-model="waitingBenchmark"></sp-global-input-search>
            </div>
          </div>
          <div class="vx-row mt-8">
            <div class="vx-col w-full sm:w-1/2">
              <p>
                Strategy:
                {{ waitingBenchmarkCleanName }} can vary between
                <span class="font-semibold"
                  >{{ waitingUnderlyingRangeInStrategy[0] }}% and
                  {{ waitingUnderlyingRangeInStrategy[1] }}%</span
                >
              </p>
              <vs-slider
                v-model="waitingUnderlyingRangeInStrategy"
                color="primary"
                ticks
                :step-decimals="false"
                :min="0"
                :max="100"
                :step="5"
                text-fixed="%"
                :disabled="loading"
              />
            </div>
          </div>
          <div class="vx-row mt-8">
            <div class="vx-col w-full sm:w-1/2">
              <p>
                Benchmark:
                <span class="font-semibold">{{ waitingBenchmarkPriceName }}</span>
              </p>
              <vs-slider
                v-model="waitingUnderlyingInBenchmark"
                :color="benchmarkColor"
                :step-decimals="false"
                :min="0"
                :max="100"
                :step="5"
                text-fixed="%"
                :disabled="loading"
              />
            </div>
          </div>
          <div class="collapse-parameters mt-4">
            <div
              class="flex justify-left items-center mb-base cursor-pointer"
              @click="toggleAdvancedParameters"
            >
              <h5 class="pr-2">Advanced</h5>
              <feather-icon
                class="arrow"
                :class="[
                  { rotate90: maxHeightOfAdvancedParameters != '0px' },
                  { rotate0: maxHeightOfAdvancedParameters == '0px' },
                ]"
                icon="ChevronRightIcon"
              />
            </div>
            <div
              ref="advanced-parameters"
              class="vx-row mb-base collapse-area"
              :style="styleAdvancedParameters"
            >
              <div class="vx-col w-full sm:w-1/2">
                <div class="w-full mb-base">
                  <span class="flex pb-2">
                    <p class="font-semibold">Underlying type</p>
                    <feather-icon
                      v-if="maxHeightOfAdvancedParameters != '0px'"
                      v-tooltip="{
                        content: underlyingTypeTooltip,
                        boundariesElement: 'window',
                      }"
                      icon="HelpCircleIcon"
                      class="w-4 ml-1"
                      @mouseenter="logTooltipUnderlyingType"
                    />
                  </span>
                  <v-select
                    v-model="underlyingType"
                    :clearable="false"
                    :options="allUnderlyingTypesOptions"
                    :reduce="(option) => option.value"
                  />
                </div>

                <div class="w-full mb-base">
                  <span class="flex pb-2">
                    <p class="font-semibold">Rebalancing frequency</p>
                    <feather-icon
                      v-if="maxHeightOfAdvancedParameters != '0px'"
                      v-tooltip="{
                        content: rebalancingFrequencyTooltip,
                        boundariesElement: 'window',
                      }"
                      icon="HelpCircleIcon"
                      class="w-4 ml-1"
                      @mouseenter="logTooltipRebalancingFrequency"
                    />
                  </span>
                  <v-select
                    v-model="rebalancingFrequency"
                    :clearable="false"
                    :options="allRebalacingFrequenciesOptions"
                    :reduce="(option) => option.value"
                  />
                </div>

                <div class="w-full mb-base">
                  <span class="flex pb-2">
                    <p class="font-semibold">Rebalancing threshold (in %)</p>
                    <feather-icon
                      v-if="maxHeightOfAdvancedParameters != '0px'"
                      v-tooltip="{
                        content: rebalancingThresholdTooltip,
                        boundariesElement: 'window',
                      }"
                      icon="HelpCircleIcon"
                      class="w-4 ml-1"
                      @mouseenter="logTooltipRebalancingThreshold"
                    />
                  </span>
                  <v-select
                    v-model="rebalancingThreshold"
                    :clearable="false"
                    :options="allRebalacingThresholdsOptions"
                    :reduce="(option) => option.value"
                  />
                </div>

                <div class="w-full mb-base">
                  <p class="font-semibold pb-2">Transaction cost (in %)</p>
                  <vs-input
                    v-model="txCost"
                    class="w-full"
                    name="txCost"
                    type="number"
                    @wheel="$event.target.blur()"
                    @blur="checkTxCost"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="vx-row mt-8">
            <div class="vx-col w-full sm:w-1/2">
              <vs-button size="medium" @click="relaunchUseCase">
                <div class="flex items-center">Run use case</div>
              </vs-button>
            </div>
          </div>
        </vx-card>
      </div>
    </div>
    <indicator
      ref="indicator"
      :name="indicatorName"
      :short-name="indicatorShortName"
      :knowledge-base-term="knowledgeBaseTerm"
      :has-full-access="hasRoleForFullAccess"
      :loading="loading"
      :dates="dates"
      :downloadable-data-indicator="downloadableDataIndicator"
      :downloadable-data-end-of-day-exposure="downloadableDataEndOfDayExposure"
      :indicator-current-level="indicatorCurrentLevel"
      :data-indicator="dataIndicator"
      :strategies-names="strategiesNames"
      :strategies-colors="strategiesColors"
      :performance-stats="performanceStats"
      :cumulative-returns-with-exposure-and-risk-level="cumulativeReturnsWithExposureAndRiskLevel"
      :exposure-data="exposureData"
      :outperformance-data="outperformanceData"
      :drawdowns-data-with-exposure="drawdownsData"
      :biggest-drawdowns="biggestDrawdowns"
      :underlying-name="benchmarkCleanName"
      :events="events"
      :locked-redirect-url="lockedRedirectUrl"
      :locked-text-content="lockedTextContent"
      :is-underlying-active="isUnderlyingActive"
      :how-we-use-indicator-text="howWeUseIndicatorText"
      @dates-change="updatePeriod"
    />
  </div>
</template>

<script>
import moment from "moment";
import vSelect from "vue-select";

import { hasAnyRole, hasAnyRoles } from "@/acl";
import { Role } from "@/acl/roles";
import {
  COLLAPSE_CLICKED_EVENT,
  TOOLTIP_DISPLAYED_EVENT,
  USE_CASE_PARAMETERS_CHANGE_EVENT,
} from "@/assets/constants/analytics";
import { DEFAULT_BENCHMARK, DEFAULT_UNDERLYING_ALLOCATION } from "@/assets/constants/global";
import { RISK_LEVEL_PROPERTY_NAME } from "@/assets/constants/indicators/alquant";
import exports from "@/assets/scss/exports.module.scss";
import Indicator from "@/components/indicators/Indicator.vue";
import SpGlobalInputSearch from "@/components/input/SpGlobalInputSearch.vue";

export default {
  name: "IndicatorUseCase",
  components: {
    "v-select": vSelect,
    Indicator,
    SpGlobalInputSearch,
  },
  props: {
    indicatorId: {
      type: String,
      required: true,
    },
    storeModule: {
      type: String,
      required: true,
    },
    indicatorName: {
      type: String,
      required: true,
    },
    indicatorShortName: {
      type: String,
      required: false,
    },
    indicatorColor: {
      type: String,
      default: exports["colors-primary"],
    },
    benchmarkColor: {
      type: String,
      default: exports["colors-benchmark"],
    },
    events: {
      type: Array,
      default: () => [],
    },
    howWeUseIndicatorText: {
      type: String,
      required: false,
    },
    rolesForFullAccess: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      // The benchmark used on the displayed graph.
      selectedBenchmark: DEFAULT_BENCHMARK,
      // The potential future benchmark, present in
      // the parameter section. When we launch the use
      // case, this waiting become the new selected.
      waitingBenchmark: DEFAULT_BENCHMARK,
      // Range (in %) we want to put in underlying in our strategy.
      underlyingRangeInStrategy: [0, 100],
      waitingUnderlyingRangeInStrategy: [0, 100],
      // Amount of underlying to put in the benchmark.
      underlyingInBenchmark: DEFAULT_UNDERLYING_ALLOCATION,
      waitingUnderlyingInBenchmark: DEFAULT_UNDERLYING_ALLOCATION,
      // The date object containing the earliest and latest available dates.
      dates: { start: null, end: null },
      // The period for which we wish to display the data; null means as long as possible.
      period: null,
      // Whether we are still getting the data to populate the graphs/tables.
      loading: false,
      // Variable to regulate if the Advance section is shown or not.
      maxHeightOfAdvancedParameters: "0px",
      // All the possible options for the underlying types.
      allUnderlyingTypesOptions: [
        { label: "Growth", value: "GROWTH" },
        { label: "Defensive", value: "DEFENSIVE" },
      ],
      // All the possible options for the rebalancing frequencies.
      allRebalacingFrequenciesOptions: [
        { label: "Only based on threshold", value: null },
        { label: "Daily", value: "DAILY" },
        { label: "Weekly", value: "WEEKLY" },
        { label: "Monthly", value: "MONTHLY" },
        { label: "Quarterly", value: "QUARTERLY" },
      ],
      // All the possible options for the rebalancing threshold.
      allRebalacingThresholdsOptions: [
        { label: "Only based on frequency", value: null },
        ...[...Array(20).keys()]
          .map((x) => x * 5 + 5)
          .map((x) => ({ value: x / 100, label: `${x}%` })),
      ],
      // Selected option in the dropdown for the underlying type.
      underlyingType: "GROWTH",
      // Selected option in the dropdown for the rebalancing frequency.
      rebalancingFrequency: "DAILY",
      // The threshold on which to trigger a rebalancing.
      rebalancingThreshold: null,
      // Transaction cost
      txCost: 0.05,
      underlyingTypeTooltip:
        'Define the type of the underlying. A "Growth" type means that the underlying is equity-like, most of the time used to make profits during bull markets. A "Defensive" type means that the underlying is used to protect capital during drawdown periods.',
      rebalancingFrequencyTooltip:
        'Define when the underlying is going to be adjusted following the indicator value. A frequency of "Daily" will look at the indicator every day and use its value to weigh the exposition to the underyling. A frequency of "Quarterly" will only check the indicator value and adjust the exposition at the beginning of each quarter.',
      rebalancingThresholdTooltip:
        'Define when to trigger an exposure rebalancing, bypassing the rebalancing frequency. If you put a threshold of 50%, then if the difference between the indicator value at the last rebalancing and the current indicator value is more or equal than this threshold, the exposure to the underlying will be adapted to the current indicator value. Note that if you set the rebalancing frequency to "Daily", changing this value won\'t have any effect.',
    };
  },
  computed: {
    isAuthenticated() {
      return hasAnyRole(Role.insight);
    },
    lockedRedirectUrl() {
      return !this.isAuthenticated ? "/auth/register" : "/help/contact";
    },
    // What to display when any graph is blurred or blocked.
    lockedTextContent() {
      return !this.isAuthenticated
        ? "Create an account to get access to this data"
        : !this.hasRoleForFullAccess
          ? "Contact us to get access to this data"
          : !this.isUnderlyingActive
            ? `Underlying not available since ${this.selectedBenchmark.last_date}`
            : "";
    },
    isUnderlyingActive() {
      return this.selectedBenchmark.active !== false;
    },
    // Whether this is for a custom indicator or not.
    isCustomIndicator() {
      return this.storeModule === "customIndicators";
    },
    // Knowledge base term associated with this indicator.
    knowledgeBaseTerm() {
      if (this.isCustomIndicator) {
        return null;
      }
      return `${this.indicatorId}-indicator`;
    },
    // Colors to be associated with each strategy or benchmark.
    strategiesColors() {
      return [this.indicatorColor, this.benchmarkColor];
    },
    // Names of benchmark + strategy.
    strategiesNames() {
      return [this.indicatorPriceName, this.benchmarkPriceName];
    },
    downloadableDataIndicator() {
      let result;
      if (this.isCustomIndicator) {
        result = this.$store.getters[`${this.storeModule}/getDownloadableCustomIndicator`](
          this.indicatorId
        );
      } else {
        result = this.$store.getters[`${this.storeModule}/getDownloadableIndicator`];
      }

      if (!result) {
        return result;
      }

      return result.map((datapoint) => ({
        date: datapoint["date"],
        value: Math.round(100 * datapoint["value"]) / 100,
      }));
    },
    downloadableDataEndOfDayExposure() {
      let result;
      if (this.isCustomIndicator) {
        result = this.$store.getters[
          `${this.storeModule}/getCustomIndicatorDownloadableEndOfDayExposure`
        ](this.indicatorId);
      } else {
        result = this.$store.getters[`${this.storeModule}/getDownloadableEndOfDayExposure`];
      }

      if (!result) {
        return result;
      }

      return result.map((datapoint) => ({
        date: datapoint["date"],
        value: Math.round(100 * datapoint["value"]) / 100,
      }));
    },
    styleAdvancedParameters() {
      return {
        maxHeight: this.maxHeightOfAdvancedParameters,
        opacity: this.maxHeightOfAdvancedParameters == "0px" ? 0 : 1,
        cursor: this.maxHeightOfAdvancedParameters == "0px" ? "default" : "auto",
      };
    },
    details() {
      if (!this.cumulativeReturnsWithExposureAndRiskLevel || !this.yearToDateStartDate) {
        return !this.cumulativeReturnsWithExposureAndRiskLevel
          ? this.cumulativeReturnsWithExposureAndRiskLevel
          : this.yearToDateStartDate;
      }

      return {
        start: moment(this.cumulativeReturnsWithExposureAndRiskLevel[0]["date"]).format(
          "YYYY-MM-DD"
        ),
        end: moment(this.cumulativeReturnsWithExposureAndRiskLevel.slice(-1)[0]["date"]).format(
          "YYYY-MM-DD"
        ),
        ytdStart: moment(this.yearToDateStartDate).format("YYYY-MM-DD"),
      };
    },
    yearToDateStartDate() {
      if (this.isCustomIndicator) {
        return this.$store.getters[`${this.storeModule}/getYearToDateStartDate`](this.indicatorId);
      }

      return this.$store.getters[`${this.storeModule}/getYearToDateStartDate`];
    },
    indicatorCurrentLevel() {
      let result;
      if (this.isCustomIndicator) {
        result = this.$store.getters[`${this.storeModule}/getCustomIndicatorCurrentLevel`](
          this.indicatorId
        );
      } else {
        result = this.$store.getters[`${this.storeModule}/getIndicatorCurrentLevel`];
      }

      if (!result) {
        return result;
      }

      return result["value"];
    },
    dataIndicator() {
      let result;
      if (this.isCustomIndicator) {
        result = this.$store.getters[`${this.storeModule}/getCustomIndicatorValues`](
          this.indicatorId
        );
      } else {
        result = this.$store.getters[`${this.storeModule}/getIndicator`];
      }

      if (!result) {
        return result;
      }

      return result.map((datapoint) => ({
        date: datapoint["date"],
        value: Math.round(100 * datapoint["value"]) / 100,
      }));
    },
    performanceStats() {
      if (this.isCustomIndicator) {
        return this.$store.getters[`${this.storeModule}/getCustomIndicatorPerformanceStats`](
          this.indicatorId
        );
      }

      return this.$store.getters[`${this.storeModule}/getPerformanceStats`];
    },
    riskLevelPropertyName() {
      return RISK_LEVEL_PROPERTY_NAME;
    },
    cumulativeReturnsWithExposureAndRiskLevel() {
      let cumulativeReturnsWithExposure;
      if (this.isCustomIndicator) {
        cumulativeReturnsWithExposure = this.$store.getters[
          `${this.storeModule}/getCustomIndicatorCumulativeReturnsWithExposure`
        ](this.indicatorId);
      } else {
        cumulativeReturnsWithExposure =
          this.$store.getters[`${this.storeModule}/getCumulativeReturnsWithExposure`];
      }

      if (!cumulativeReturnsWithExposure || !this.dataIndicator) {
        return !cumulativeReturnsWithExposure ? cumulativeReturnsWithExposure : this.dataIndicator;
      }

      if (cumulativeReturnsWithExposure.length != this.dataIndicator.length) {
        return;
      }

      return cumulativeReturnsWithExposure.map((cum, idx) => {
        const tmp = { ...cum };
        tmp[this.riskLevelPropertyName] = this.dataIndicator[idx]["value"];
        return tmp;
      });
    },
    outperformanceData() {
      if (this.isCustomIndicator) {
        return this.$store.getters[`${this.storeModule}/getCustomIndicatorOutperformanceData`](
          this.indicatorId
        );
      }

      return this.$store.getters[`${this.storeModule}/getOutperformanceData`];
    },
    drawdownsData() {
      if (this.isCustomIndicator) {
        return this.$store.getters[`${this.storeModule}/getCustomIndicatorDrawdownsData`](
          this.indicatorId
        );
      }

      return this.$store.getters[`${this.storeModule}/getDrawdownsData`];
    },
    biggestDrawdowns() {
      if (this.isCustomIndicator) {
        return this.$store.getters[`${this.storeModule}/getCustomIndicatorBiggestDrawdownsData`](
          this.indicatorId
        );
      }

      return this.$store.getters[`${this.storeModule}/getBiggestDrawdownsData`];
    },
    benchmarkCleanName() {
      return ["predefined", "index"].includes(this.selectedBenchmark["exchange"])
        ? this.selectedBenchmark["name"]
        : this.selectedBenchmark["ticker"];
    },
    waitingBenchmarkCleanName() {
      return ["predefined", "index"].includes(this.waitingBenchmark["exchange"])
        ? this.waitingBenchmark["name"]
        : this.waitingBenchmark["ticker"];
    },
    benchmarkCurrency() {
      return this.selectedBenchmark["currency"];
    },
    waitingBenchmarkCurrency() {
      return this.waitingBenchmark["currency"];
    },
    benchmarkPriceName() {
      return `${this.underlyingInBenchmark}% ${this.benchmarkCleanName} + ${
        100 - this.underlyingInBenchmark
      }% ${this.benchmarkCurrency} cash`;
    },
    waitingBenchmarkPriceName() {
      return `${this.waitingUnderlyingInBenchmark}% ${
        this.waitingBenchmarkCleanName
      } + ${100 - this.waitingUnderlyingInBenchmark}% ${this.waitingBenchmarkCurrency} cash`;
    },
    indicatorPriceName() {
      return `${this.benchmarkCleanName} using ${this.indicatorName}`;
    },
    exposureData() {
      if (!this.cumulativeReturnsWithExposureAndRiskLevel) {
        return this.cumulativeReturnsWithExposureAndRiskLevel;
      }

      return this.cumulativeReturnsWithExposureAndRiskLevel.map((obj) => {
        const newObj = { date: obj["date"] };
        newObj[this.benchmarkCleanName] = obj["exposure_value"];
        return newObj;
      });
    },
    hasRoleForFullAccess() {
      return this.rolesForFullAccess == null || hasAnyRoles(this.rolesForFullAccess);
    },
  },
  watch: {
    details(newValue) {
      // Set dates if the period is not set (i.e. when we change of benchmark).
      if (newValue && !this.period) {
        this.dates = newValue;
      }
    },
    loading(newValue) {
      if (newValue) {
        this.$refs["parameters"].refreshcard();
      } else {
        this.$refs["parameters"].removeRefreshAnimation(200);
      }
    },
    txCost(newValue) {
      // Validation to make sure that the value inputed for
      // the threshold is always between 0 and 100.
      const newValueFloat = parseFloat(newValue);
      if (newValueFloat < 0) {
        this.txCost = 0;
      } else if (newValueFloat > 100) {
        this.txCost = 100;
      }
    },
  },
  // We need to clean the data from the store every time, otherwise,
  // if we go back and forth from the same page of the one indicator
  // the computed method will trigger while we are computing a backtest
  // behind the hood, which can give us random behavior.
  beforeMount() {
    if (this.isCustomIndicator) {
      this.$store.dispatch(`${this.storeModule}/resetCustomIndicatorDataState`);
    } else {
      this.$store.dispatch(`${this.storeModule}/resetIndicatorDataState`);
    }
  },
  mounted() {
    this.resetAndRunBacktest();
  },
  methods: {
    toggleAdvancedParameters() {
      this.$analytics.logEvent(COLLAPSE_CLICKED_EVENT, {
        collapse_title: "Advanced",
      });
      const maxHeightx = this.$refs["advanced-parameters"].scrollHeight;
      if (this.maxHeightOfAdvancedParameters == "0px") {
        this.maxHeightOfAdvancedParameters = `${maxHeightx}px`;
      } else {
        this.maxHeightOfAdvancedParameters = "0px";
      }
    },
    checkTxCost() {
      // If the user leave the input of the threshold having
      // completely removed the value inside, we put back the
      // original value.
      if (!this.txCost) {
        this.txCost = 0.05;
      }
    },
    relaunchUseCase() {
      // Validate that there is at least a threshold or a frequency.
      if (this.rebalancingThreshold === null && this.rebalancingFrequency === null) {
        this.$vs.notify({
          title: "Action aborted",
          text: "You can't have no threshold and no frequency for a use case.",
          color: "warning",
          time: 5000,
        });
        return;
      }

      this.$analytics.logEvent(USE_CASE_PARAMETERS_CHANGE_EVENT, {
        indicator: this.indicatorId,
        min_in_underlying: this.underlyingRangeInStrategy[0],
        max_in_underlying: this.underlyingRangeInStrategy[1],
        underlying_in_benchmark: this.underlyingInBenchmark,
      });
      this.underlyingRangeInStrategy = this.waitingUnderlyingRangeInStrategy;
      this.underlyingInBenchmark = this.waitingUnderlyingInBenchmark;
      this.selectedBenchmark = this.waitingBenchmark;
      this.resetAndRunBacktest();
    },
    resetIndicatorState() {
      if (this.isCustomIndicator) {
        return this.$store.dispatch(`${this.storeModule}/resetCustomIndicatorDataState`);
      }

      return this.$store.dispatch(`${this.storeModule}/resetIndicatorDataState`);
    },
    runBacktest() {
      const params = {
        benchmarkId: this.selectedBenchmark["id"],
        benchmarkExchange: this.selectedBenchmark["exchange"],
        period: this.period,
        currency: this.selectedBenchmark["currency"],
        minInUnderlying: this.underlyingRangeInStrategy[0] / 100,
        maxInUnderlying: this.underlyingRangeInStrategy[1] / 100,
        underlyingInBenchmark: this.underlyingInBenchmark / 100,
        underlyingType: this.underlyingType,
        rebalancingFrequency: this.rebalancingFrequency,
        rebalancingThreshold: this.rebalancingThreshold,
        txCost: this.txCost / 100,
      };
      if (this.isCustomIndicator) {
        params["customIndicatorId"] = this.indicatorId;
      } else {
        params["indicatorId"] = this.indicatorId;
      }
      return this.$store.dispatch(`${this.storeModule}/runBacktest`, params);
    },
    resetAndRunBacktest(withReset = true) {
      this.loading = true;

      // We want to load the new corresponding data and reset the
      // zoom on the indicator's graphs.
      if (withReset) {
        this.resetIndicatorState();
        this.period = null;
        this.$refs["indicator"].changeZoomButton("reset");
      }

      // While the above allows for interaction like using the mouse wheel or
      // the arrow keys to be disable, if the user hasn't released the mouse
      // then it still can move (while being "disable"), hence we create a
      // manual mouseevent to trigger a mouse up event and thus disable
      // the potential (but not necessary) ongoing mouse down event.
      const event = new MouseEvent("mouseup", {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      const cb = this.$refs["parameters"].$el;
      cb.dispatchEvent(event);
      this.runBacktest().then(() => {
        this.loading = false;
      });
    },
    updatePeriod(newPeriod) {
      this.period = newPeriod;
      this.resetAndRunBacktest(false);
    },
    logTooltipUnderlyingType() {
      this.$analytics.logEvent(TOOLTIP_DISPLAYED_EVENT, {
        tooltip: `use_case_underlying_type`,
      });
    },
    logTooltipRebalancingFrequency() {
      this.$analytics.logEvent(TOOLTIP_DISPLAYED_EVENT, {
        tooltip: `use_case_rebalancing_frequency`,
      });
    },
    logTooltipRebalancingThreshold() {
      this.$analytics.logEvent(TOOLTIP_DISPLAYED_EVENT, {
        tooltip: `use_case_rebalancing_threshold`,
      });
    },
  },
};
</script>
